'use client';

import { PropsWithChildren, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { InternalAxiosRequestConfig } from 'axios';
import { setCookie } from 'cookies-next/client';

import { parseJwt, useUserStore } from '@entities/user';
import { privateApi } from '@shared/config/axios.config';

const AccountProvider = (props: PropsWithChildren) => {
  const { children } = props;

  const referralCode = useSearchParams().get('referral_code');

  const setAuthenticated = useUserStore((state) => state.setAuthenticated);

  const requestInterceptor = useRef<number>(-1);
  const responseInterceptor = useRef<number>(-1);

  const { isSignedIn, getToken, signOut } = useAuth();

  useEffect(() => {
    if (referralCode) {
      setCookie('referral_code', referralCode, { maxAge: Number.MAX_SAFE_INTEGER });
    }
  }, [referralCode]);

  const waitForToken = (): Promise<string> => {
    return new Promise((resolve) => {
      const interval = setInterval(async () => {
        const token = await getToken({ template: 'withMetadata' });

        if (token) {
          const parsedToken = parseJwt(token);

          if (parsedToken?.custom_claims?.backend_id) {
            clearInterval(interval);
            resolve(token);
          }
        }
      }, 300);
    });
  };

  useEffect(() => {
    if (isSignedIn) {
      waitForToken()
        .then((token) => {
          if (!token) {
            throw new Error();
          }

          requestInterceptor.current = privateApi.interceptors.request.use(
            (config) => {
              return {
                ...config,
                headers: { ...config.headers, Authorization: `Bearer ${token}` },
              } as InternalAxiosRequestConfig;
            },
            (error) => Promise.reject(error),
          );

          responseInterceptor.current = privateApi.interceptors.response.use(
            (response) => response,
            (error) => {
              if (error?.response?.status === 401) {
                signOut();
              }

              return Promise.reject(error);
            },
          );

          setAuthenticated(true);
        })
        .catch(() => {
          setAuthenticated(false);

          privateApi.interceptors.request.eject(requestInterceptor.current);
          privateApi.interceptors.response.eject(responseInterceptor.current);
        });
    } else {
      setAuthenticated(false);

      privateApi.interceptors.request.eject(requestInterceptor.current);
      privateApi.interceptors.response.eject(responseInterceptor.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  return children;
};

export default AccountProvider;
